/**
 * Competitor pricing registry (wave 2 commercial pages).
 *
 * Every value comes from the vendor's official pricing / help page listed in `sourceUrls`, checked on
 * `lastVerified`. Pricing goes stale fast: pages that read this registry call
 * `warnStaleEntries('competitor-pricing', COMPETITOR_PRICING, COMPETITOR_PRICING_MAX_AGE_DAYS)`.
 *
 * Rules:
 * - Only render entries where `isPublishable(entry)` is true. Drafts (unverified numbers) never render.
 * - Never derive a number the vendor does not publish (no FX conversion, no guessed seat-pack prices).
 *   When a cost can't be computed, `getMonthlyCostForSeats` returns `total: null` and a note.
 */
import type { PseoEntryBase } from './pseo/types';
import { isPublishable } from './pseo/types';

export const COMPETITOR_PRICING_MAX_AGE_DAYS = 60;

export type CompetitorId =
  | 'frame-io'
  | 'filestage'
  | 'ziflow'
  | 'wipster'
  | 'dropbox-replay'
  | 'pageproof'
  | 'govisually';

export type PricingBasis = 'per-seat' | 'flat' | 'add-on';
export type PricingCurrency = 'USD' | 'EUR';

export interface CompetitorPlan {
  name: string;
  /** Month-to-month list price (per seat when `unit` is `per-seat`). `null` = custom or not listed. */
  priceMonthly: number | null;
  /** Monthly price when billed annually, only when the vendor lists it. */
  priceAnnualMonthly?: number;
  /** Display copy, as stated by the vendor. */
  seats: string;
  storage: string;
  reviewers: string;
  notes?: string;
  /** How the price applies: per seat, or one price for the account. */
  unit: 'per-seat' | 'flat';
  /** Highest seat count the plan allows. `null` = no stated limit. Omitted = not stated (left out of cost math). */
  maxSeats?: number | null;
  /** Minimum seats billed (e.g. GoVisually Pro). */
  minSeats?: number;
  /** Flat plans: seats included in the price. */
  includedSeats?: number;
  /** Flat plans: extra seats sold in packs. `price: null` when the vendor doesn't list the pack price. */
  extraSeatPack?: { size: number; price: number | null };
  isFree?: boolean;
  /** The plan can't review video (e.g. GoVisually Lite). */
  excludesVideo?: boolean;
}

export interface RegionalPricing {
  currency: PricingCurrency;
  label: string;
  priceMonthly: number;
  priceAnnualMonthly?: number;
  sourceUrl: string;
}

export interface CompetitorPricing extends PseoEntryBase {
  id: CompetitorId;
  vendor: string;
  vendorUrl: string;
  basis: PricingBasis;
  currency: PricingCurrency;
  plans: CompetitorPlan[];
  freePlan: string | null;
  trial: string | null;
  gotchas: string[];
  /** Pricing seen on a regional official page while the main-currency price is still unverified. */
  regionalPricing?: RegionalPricing;
}

const VERIFIED_ON = '2026-10-21';
const INITIAL_CHANGELOG = [{ date: VERIFIED_ON, change: 'Initial entry from the official pricing page.' }];

export const COMPETITOR_PRICING: CompetitorPricing[] = [
  {
    id: 'frame-io',
    slug: 'frame-io-pricing',
    primaryQuery: 'frame.io pricing',
    vendor: 'Frame.io',
    vendorUrl: 'https://www.frame.io/pricing',
    basis: 'per-seat',
    currency: 'USD',
    plans: [
      {
        name: 'Free',
        priceMonthly: 0,
        seats: 'Up to 2 members',
        storage: '2GB (+2GB mounted)',
        reviewers: 'Share reviewers need no seat',
        notes: '2 projects, 1 workspace',
        unit: 'per-seat',
        maxSeats: 2,
        isFree: true,
      },
      {
        name: 'Pro',
        priceMonthly: 15,
        seats: 'Up to 5 members',
        storage: '2TB, +2TB per added member',
        reviewers: 'Share reviewers need no seat',
        notes: 'Per member per month, plus tax',
        unit: 'per-seat',
        maxSeats: 5,
      },
      {
        name: 'Team',
        priceMonthly: 25,
        seats: 'Up to 15 members',
        storage: '3TB, +2TB per added member',
        reviewers: 'Share reviewers need no seat',
        notes: 'Per member per month, plus tax. Adds restricted projects and internal comments',
        unit: 'per-seat',
        maxSeats: 15,
      },
      {
        name: 'Enterprise',
        priceMonthly: null,
        seats: 'Custom',
        storage: 'Custom',
        reviewers: 'Share reviewers need no seat',
        notes: 'Select and Prime tiers, custom pricing',
        unit: 'per-seat',
        maxSeats: null,
      },
    ],
    freePlan: 'Free plan: up to 2 members, 2GB storage (+2GB mounted), 2 projects and 1 workspace.',
    trial: 'Pro and Team trials (length not stated; billing info required).',
    gotchas: [
      'Annual billing saves 13%, but the pricing page does not show the annual price.',
      'Prices are per member per month and exclude tax.',
      'More than 15 members means Enterprise, which has custom pricing.',
      'Free plan accounts over the 2-project limit see a "Project limit exceeded" banner and may be locked after a grace period.',
      'Watermarking: Pro and Team include 1 static template; Enterprise includes unlimited templates.',
    ],
    sourceUrls: [
      { label: 'Frame.io pricing', url: 'https://www.frame.io/pricing' },
      { label: 'Frame.io help: plan limits', url: 'https://help.frame.io/en/articles/2673548-plan-limits-in-frame-io' },
    ],
    lastVerified: VERIFIED_ON,
    status: 'verified',
    changelog: INITIAL_CHANGELOG,
    faqs: [
      {
        question: "How much does Frame.io cost?",
        answer:
          "Frame.io has a Free plan, Pro at $15 per member per month and Team at $25 per member per month, plus tax. Enterprise (Select and Prime) has custom pricing. Annual billing saves 13%, but the pricing page doesn't show the annual price.",
      },
      {
        question: "How much does Frame.io cost for a team of 5?",
        answer:
          "On Pro, 5 members cost $75 per month (5 × $15). Pro allows up to 5 members, so a sixth member means Team at $25 per member: 6 members cost $150 per month.",
      },
      {
        question: "What are the Frame.io free plan limits?",
        answer:
          "The Free plan allows up to 2 members, 2GB of storage (+2GB mounted), 2 projects and 1 workspace. Accounts over the project limit see a \"Project limit exceeded\" banner and may be locked after a grace period.",
      },
      {
        question: "Do reviewers need a paid Frame.io seat?",
        answer:
          "No. Frame.io says share reviewers need no seat, and reviewers can comment on a share without an account. Frame.io asks for a name and email after the first comment.",
      },
      {
        question: "What happens when a Frame.io team grows past 15 members?",
        answer:
          "Team allows up to 15 members. Larger teams move to Enterprise, which has custom pricing, so there is no listed price above 15 members.",
      },
      {
        question: "Does Frame.io have a free trial?",
        answer:
          "Pro and Team have trials. The pricing page doesn't state the trial length, and billing info is required to start one.",
      },
    ],
  },
  {
    id: 'filestage',
    slug: 'filestage-pricing',
    primaryQuery: 'filestage pricing',
    vendor: 'Filestage',
    vendorUrl: 'https://filestage.io/pricing',
    basis: 'flat',
    currency: 'EUR',
    plans: [
      {
        name: 'Free',
        priceMonthly: 0,
        seats: '10 seats',
        storage: '2GB',
        reviewers: 'Unlimited, no account needed',
        notes: '1 active project, 5 new files per month, 2 reviewer groups',
        unit: 'flat',
        maxSeats: 10,
        includedSeats: 10,
        isFree: true,
      },
      {
        name: 'Starter',
        priceMonthly: 199,
        seats: '10 seats, extra seats in packs of 5',
        storage: '1TB',
        reviewers: 'Unlimited, no account needed',
        unit: 'flat',
        maxSeats: null,
        includedSeats: 10,
        extraSeatPack: { size: 5, price: null },
      },
      {
        name: 'Business',
        priceMonthly: 329,
        seats: '10 seats, extra seats in packs of 5',
        storage: '3TB',
        reviewers: 'Unlimited, no account needed',
        unit: 'flat',
        maxSeats: null,
        includedSeats: 10,
        extraSeatPack: { size: 5, price: null },
      },
      {
        name: 'Enterprise',
        priceMonthly: null,
        seats: '10 seats, extra seats in packs of 5',
        storage: '10TB',
        reviewers: 'Unlimited, no account needed',
        notes: 'Custom pricing',
        unit: 'flat',
        maxSeats: null,
      },
    ],
    freePlan: 'Free plan: 1 active project, 5 new files per month, 2 reviewer groups and 2GB storage.',
    trial: '14-day trial, no credit card.',
    gotchas: [
      'Prices are in EUR.',
      'Every plan includes 10 seats. More seats come in packs of 5 (each pack adds 1TB), and the pack price is not listed.',
      'Annual billing gives two months free.',
    ],
    sourceUrls: [{ label: 'Filestage pricing', url: 'https://filestage.io/pricing' }],
    lastVerified: VERIFIED_ON,
    status: 'verified',
    changelog: INITIAL_CHANGELOG,
    faqs: [
      {
        question: "How much does Filestage cost?",
        answer:
          "Filestage has a Free plan (€0), Starter at €199 per month, Business at €329 per month and a custom-priced Enterprise plan. Prices are in EUR, and annual billing gives two months free.",
      },
      {
        question: "How many users are included in Filestage plans?",
        answer:
          "Every plan includes 10 seats. More seats come in packs of 5, and each pack adds 1TB of storage. The pack price isn't listed on the pricing page.",
      },
      {
        question: "What are the Filestage free plan limits?",
        answer:
          "The Free plan allows 1 active project, 5 new files per month, 2 reviewer groups and 2GB of storage.",
      },
      {
        question: "Do Filestage reviewers need an account?",
        answer:
          "No. Filestage lists unlimited reviewers on every plan, and reviewers don't need an account.",
      },
      {
        question: "Does Filestage have a free trial?",
        answer:
          "Yes. Filestage offers a 14-day trial without a credit card.",
      },
      {
        question: "Is Filestage cheaper per person than per-seat tools?",
        answer:
          "It depends on team size. With all 10 included seats in use, Starter's flat €199 works out to €19.90 per seat per month. A team of 3 still pays the full €199.",
      },
    ],
  },
  {
    id: 'ziflow',
    slug: 'ziflow-pricing',
    primaryQuery: 'ziflow pricing',
    vendor: 'Ziflow',
    vendorUrl: 'https://www.ziflow.com/pricing',
    basis: 'flat',
    currency: 'USD',
    plans: [
      {
        name: 'Free',
        priceMonthly: 0,
        seats: '2 seats',
        storage: '2GB',
        reviewers: 'Unlimited',
        notes: '1 workflow stage, 60-day history',
        unit: 'flat',
        maxSeats: 2,
        includedSeats: 2,
        isFree: true,
      },
      {
        name: 'Standard',
        priceMonthly: null,
        priceAnnualMonthly: 199,
        seats: '15 seats, extra seats in packs of 5',
        storage: '1TB',
        reviewers: 'Unlimited',
        notes: 'Billed annually',
        unit: 'flat',
        maxSeats: null,
        includedSeats: 15,
        extraSeatPack: { size: 5, price: null },
      },
      {
        name: 'Pro',
        priceMonthly: null,
        priceAnnualMonthly: 329,
        seats: '20 seats, extra seats in packs of 5',
        storage: '2TB',
        reviewers: 'Unlimited',
        notes: 'Billed annually',
        unit: 'flat',
        maxSeats: null,
        includedSeats: 20,
        extraSeatPack: { size: 5, price: null },
      },
      {
        name: 'Enterprise',
        priceMonthly: null,
        seats: '25+ seats',
        storage: '4TB+',
        reviewers: 'Unlimited',
        notes: 'Custom pricing. Includes Secure Guest Reviewing',
        unit: 'flat',
        maxSeats: null,
      },
    ],
    freePlan: 'Free forever: 2 seats, 2GB storage, 1 workflow stage and 60-day history.',
    trial: 'Trial available (length not stated).',
    gotchas: [
      'Listed prices are billed annually. The annual discount is 20% on Standard and 17.5% on Pro, so month-to-month costs more.',
      'Secure Guest Reviewing is Enterprise only.',
      'Extra seats come in packs of 5, and the pack price is not listed.',
    ],
    sourceUrls: [{ label: 'Ziflow pricing', url: 'https://www.ziflow.com/pricing' }],
    lastVerified: VERIFIED_ON,
    status: 'verified',
    changelog: INITIAL_CHANGELOG,
    faqs: [
      {
        question: "How much does Ziflow cost?",
        answer:
          "Ziflow has a Free plan ($0), Standard at $199 per month and Pro at $329 per month, both billed annually, and a custom-priced Enterprise plan.",
      },
      {
        question: "Can I pay for Ziflow monthly?",
        answer:
          "The pricing page lists billed-annually prices. Ziflow states an annual discount of 20% on Standard and 17.5% on Pro, so paying month to month costs more than the listed price.",
      },
      {
        question: "How many seats does each Ziflow plan include?",
        answer:
          "Free includes 2 seats, Standard 15, Pro 20 and Enterprise 25 or more. Extra seats come in packs of 5; the pack price isn't listed.",
      },
      {
        question: "What does the Ziflow free plan include?",
        answer:
          "The free-forever plan includes 2 seats, 2GB of storage, 1 workflow stage and 60 days of history.",
      },
      {
        question: "Do Ziflow reviewers need a paid seat?",
        answer:
          "No. Every Ziflow plan lists unlimited reviewers. The Secure Guest Reviewing feature is Enterprise only.",
      },
    ],
  },
  {
    id: 'wipster',
    slug: 'wipster-pricing',
    primaryQuery: 'wipster pricing',
    vendor: 'Wipster',
    vendorUrl: 'https://wipster.io/pricing',
    basis: 'per-seat',
    currency: 'USD',
    plans: [
      {
        name: 'Light',
        priceMonthly: 11.95,
        priceAnnualMonthly: 9.95,
        seats: 'Not stated',
        storage: '50GB',
        reviewers: 'Unlimited',
        notes: 'One price per month',
        unit: 'flat',
      },
      {
        name: 'Team',
        priceMonthly: 25,
        priceAnnualMonthly: 19.95,
        seats: 'No user limit',
        storage: 'From 250GB (up to 1TB)',
        reviewers: 'Unlimited',
        notes: 'Per user per month',
        unit: 'per-seat',
        maxSeats: null,
      },
      {
        name: 'Enterprise',
        priceMonthly: null,
        seats: 'Custom',
        storage: 'From 1TB',
        reviewers: 'Unlimited',
        notes: 'Custom pricing. Includes SSO',
        unit: 'per-seat',
        maxSeats: null,
      },
    ],
    freePlan: null,
    trial: 'Trial, no credit card.',
    gotchas: [
      'No free plan is listed on the Wipster pricing page.',
      'The Light plan does not state how many users it covers.',
      'SSO is Enterprise only.',
    ],
    sourceUrls: [{ label: 'Wipster pricing', url: 'https://wipster.io/pricing' }],
    lastVerified: VERIFIED_ON,
    status: 'verified',
    changelog: INITIAL_CHANGELOG,
    faqs: [
      {
        question: "How much does Wipster cost?",
        answer:
          "Wipster Light is $11.95 per month ($9.95 billed annually), Team is $25 per user per month ($19.95 billed annually) and Enterprise has custom pricing.",
      },
      {
        question: "Does Wipster have a free plan?",
        answer:
          "No free plan is listed on Wipster's pricing page. Wipster offers a trial without a credit card.",
      },
      {
        question: "How much does Wipster cost for a team of 5?",
        answer:
          "On Team, 5 users cost $125 per month (5 × $25), or $99.75 per month billed annually (5 × $19.95).",
      },
      {
        question: "Is there a user limit on Wipster Team?",
        answer:
          "No. Wipster Team has no user limit. The Light plan doesn't state how many users it covers.",
      },
      {
        question: "How much storage does Wipster include?",
        answer:
          "Light includes 50GB, Team starts at 250GB (up to 1TB) and Enterprise starts at 1TB. SSO is Enterprise only.",
      },
    ],
  },
  {
    id: 'dropbox-replay',
    slug: 'dropbox-replay-pricing',
    primaryQuery: 'dropbox replay pricing',
    vendor: 'Dropbox Replay',
    vendorUrl: 'https://www.dropbox.com/replay',
    basis: 'add-on',
    currency: 'USD',
    plans: [
      {
        name: 'Replay add-on',
        // USD seen only in a search snippet, not on a fetched official page. Keeps this entry in draft.
        priceMonthly: 12,
        priceAnnualMonthly: 10,
        seats: 'Per user, added to a Dropbox plan',
        storage: 'Uploads up to 150GB; video up to 12 hours',
        reviewers: 'Not stated',
        notes: 'Requires Dropbox Plus or higher; not available on Family',
        unit: 'per-seat',
        maxSeats: null,
      },
    ],
    freePlan: 'Limited free access: 4 or 10 file uploads without the add-on, depending on the Dropbox plan.',
    trial: null,
    gotchas: [
      'Replay is an add-on: you also pay for the Dropbox plan it is added to.',
      'Requires Dropbox Plus or higher and is not available on Family plans.',
      'Without the add-on you get 4 or 10 file uploads, depending on the plan.',
    ],
    regionalPricing: {
      currency: 'EUR',
      label: 'EU pricing page',
      priceMonthly: 11,
      priceAnnualMonthly: 9,
      sourceUrl: 'https://www.dropbox.com/replay',
    },
    sourceUrls: [
      { label: 'Dropbox Replay', url: 'https://www.dropbox.com/replay' },
      { label: 'Dropbox help: Replay add-on', url: 'https://help.dropbox.com/installs/dropbox-replay-add-on' },
    ],
    lastVerified: VERIFIED_ON,
    // USD price unverified (search snippet only; the fetched page showed EU pricing). Re-verify on the US page.
    status: 'draft',
    changelog: [{ date: VERIFIED_ON, change: 'Draft: USD price unverified; EU page showed €9 annual / €11 monthly.' }],
    faqs: [
      {
        question: "How is Dropbox Replay priced?",
        answer:
          "Dropbox Replay is a per-user add-on to a Dropbox plan, billed monthly or annually. The price varies by region, so check Dropbox's Replay page for your country.",
      },
      {
        question: "Which Dropbox plans can add Replay?",
        answer:
          "The Replay add-on requires Dropbox Plus or higher and isn't available on Family plans. You also pay for the Dropbox plan it's added to.",
      },
      {
        question: "Can I use Dropbox Replay without the add-on?",
        answer:
          "Yes, with limits. Without the add-on you get 4 or 10 file uploads, depending on your Dropbox plan.",
      },
      {
        question: "What are the Dropbox Replay upload limits?",
        answer:
          "With the add-on, uploads can be up to 150GB, and videos up to 12 hours long.",
      },
    ],
  },
  {
    id: 'pageproof',
    slug: 'pageproof-pricing',
    primaryQuery: 'pageproof pricing',
    vendor: 'PageProof',
    vendorUrl: 'https://pageproof.com/pricing',
    basis: 'flat',
    currency: 'USD',
    plans: [
      {
        name: 'Team',
        priceMonthly: 249,
        seats: 'Unlimited users',
        storage: 'Unlimited',
        reviewers: 'Unlimited',
        notes: '$2,499 billed yearly',
        unit: 'flat',
        maxSeats: null,
      },
      {
        name: 'Team Plus',
        priceMonthly: 499,
        seats: 'Unlimited users',
        storage: 'Unlimited',
        reviewers: 'Unlimited',
        notes: '$4,999 billed yearly. Includes SSO',
        unit: 'flat',
        maxSeats: null,
      },
      {
        name: 'Enterprise',
        priceMonthly: null,
        seats: 'Unlimited users',
        storage: 'Unlimited',
        reviewers: 'Unlimited',
        notes: 'Quote on request',
        unit: 'flat',
        maxSeats: null,
      },
    ],
    freePlan: null,
    trial: 'Trial on request.',
    gotchas: ['No free plan.', 'SSO starts on Team Plus.'],
    sourceUrls: [{ label: 'PageProof pricing', url: 'https://pageproof.com/pricing' }],
    lastVerified: VERIFIED_ON,
    status: 'verified',
    changelog: INITIAL_CHANGELOG,
    faqs: [],
  },
  {
    id: 'govisually',
    slug: 'govisually-pricing',
    primaryQuery: 'govisually pricing',
    vendor: 'GoVisually',
    vendorUrl: 'https://govisually.com/pricing/',
    basis: 'per-seat',
    currency: 'USD',
    plans: [
      {
        name: 'Lite',
        priceMonthly: 16,
        seats: '1–5 users',
        storage: '50GB',
        reviewers: 'Up to 15 external reviewers',
        notes: '15 active projects, no video',
        unit: 'per-seat',
        maxSeats: 5,
        excludesVideo: true,
      },
      {
        name: 'Pro',
        priceMonthly: 33,
        seats: 'Minimum 3 users',
        storage: '150GB',
        reviewers: 'Unlimited',
        unit: 'per-seat',
        maxSeats: null,
        minSeats: 3,
      },
      {
        name: 'CPG Compliance AI Suite',
        priceMonthly: null,
        seats: 'Custom',
        storage: 'Custom',
        reviewers: 'Not stated',
        notes: 'Custom pricing',
        unit: 'per-seat',
        maxSeats: null,
      },
    ],
    freePlan: null,
    trial: 'Trial, no credit card.',
    gotchas: [
      'The Lite plan does not include video review.',
      'Lite allows 15 active projects and up to 15 external reviewers.',
      'Pro bills a minimum of 3 users.',
    ],
    sourceUrls: [{ label: 'GoVisually pricing', url: 'https://govisually.com/pricing/' }],
    lastVerified: VERIFIED_ON,
    status: 'verified',
    changelog: INITIAL_CHANGELOG,
    faqs: [],
  },
];

export function getCompetitorPricing(id: CompetitorId): CompetitorPricing {
  const entry = COMPETITOR_PRICING.find((item) => item.id === id);
  if (!entry) {
    throw new Error(`Unknown competitor pricing entry: ${id}`);
  }
  return entry;
}

/** Entries safe to render, link and list in the sitemap. */
export const getPublishedCompetitorPricing = () => COMPETITOR_PRICING.filter(isPublishable);

export const competitorPricingPath = (entry: Pick<CompetitorPricing, 'slug'>) => `/comparisons/${entry.slug}`;

/** "$15", "$11.95", "€199". */
export function formatPricingAmount(amount: number, currency: PricingCurrency): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

export interface SeatCostOptions {
  /** Include free plans (off by default: free plans carry project/file limits most teams outgrow). */
  includeFree?: boolean;
  /** Skip plans that can't review video. */
  requireVideo?: boolean;
}

export interface SeatCost {
  /** Plan the total is based on; `null` when no listed plan fits. */
  planName: string | null;
  /** Monthly total in `currency`; `null` when it can't be computed from published prices. */
  total: number | null;
  currency: PricingCurrency;
  /** `annual` when only the billed-annually price is listed. */
  billing: 'monthly' | 'annual' | null;
  note: string;
}

const planPrice = (plan: CompetitorPlan) => plan.priceMonthly ?? plan.priceAnnualMonthly ?? null;

/**
 * What a team of `seats` pays per month on the cheapest listed plan that fits, using the plan rules:
 * per-seat price × seats (respecting seat caps and minimums), flat price when the seats are included,
 * flat price + seat packs when the pack price is listed. Returns `total: null` and a note otherwise.
 */
export function getMonthlyCostForSeats(
  entry: CompetitorPricing,
  seats: number,
  options: SeatCostOptions = {},
): SeatCost {
  const count = Math.max(1, Math.ceil(seats));
  const fmt = (amount: number) => formatPricingAmount(amount, entry.currency);
  const addOnNote = entry.basis === 'add-on' ? ' Add-on: the base plan is billed separately.' : '';

  const candidates: SeatCost[] = [];
  const blockers: string[] = [];

  for (const plan of entry.plans) {
    if (plan.isFree && !options.includeFree) continue;
    if (options.requireVideo && plan.excludesVideo) continue;
    if (plan.maxSeats === undefined) continue;
    if (plan.maxSeats !== null && count > plan.maxSeats) continue;

    const price = planPrice(plan);
    if (price === null) {
      blockers.push(`${count} seats needs ${plan.name}, which has custom pricing.`);
      continue;
    }
    const billing = plan.priceMonthly === null || plan.priceMonthly === undefined ? 'annual' : 'monthly';
    const billingNote = billing === 'annual' ? ', billed annually' : '';

    if (plan.unit === 'per-seat') {
      const billedSeats = Math.max(count, plan.minSeats ?? 1);
      const minNote = billedSeats > count ? ` (minimum ${plan.minSeats} seats)` : '';
      candidates.push({
        planName: plan.name,
        total: price * billedSeats,
        currency: entry.currency,
        billing,
        note: `${plan.name}: ${billedSeats} × ${fmt(price)} per seat${minNote}${billingNote}.${addOnNote}`,
      });
      continue;
    }

    if (plan.includedSeats === undefined || count <= plan.includedSeats) {
      const included = plan.includedSeats === undefined ? '' : `, includes ${plan.includedSeats} seats`;
      candidates.push({
        planName: plan.name,
        total: price,
        currency: entry.currency,
        billing,
        note: `${plan.name}: flat ${fmt(price)}${included}${billingNote}.${addOnNote}`,
      });
      continue;
    }

    const pack = plan.extraSeatPack;
    if (!pack) continue;
    if (pack.price === null) {
      blockers.push(
        [
          `${plan.name} includes ${plan.includedSeats} seats; extra seats come in packs of ${pack.size} and the `,
          `pack price is not listed.`,
        ].join(''),
      );
      continue;
    }
    const packs = Math.ceil((count - plan.includedSeats) / pack.size);
    candidates.push({
      planName: plan.name,
      total: price + packs * pack.price,
      currency: entry.currency,
      billing,
      note: `${plan.name}: flat ${fmt(price)} + ${packs} × ${fmt(pack.price)} seat pack${billingNote}.${addOnNote}`,
    });
  }

  const cheapest = candidates.sort((a, b) => (a.total ?? 0) - (b.total ?? 0))[0];
  if (cheapest) {
    return cheapest;
  }

  return {
    planName: null,
    total: null,
    currency: entry.currency,
    billing: null,
    note: blockers[0] ?? `No listed ${entry.vendor} plan covers ${count} seats; pricing is custom.`,
  };
}

/** Competitors with a dedicated `/comparisons/<vendor>-pricing` page. Only linked while the entry is publishable. */
const PRICING_PAGE_IDS = new Set<CompetitorId>(['frame-io', 'filestage', 'ziflow', 'wipster', 'dropbox-replay']);

export const hasPublishedPricingPage = (entry: CompetitorPricing) =>
  PRICING_PAGE_IDS.has(entry.id) && isPublishable(entry);

/** Lowest listed paid price, e.g. "$15 per seat/month (Pro)" or "€199/month flat (Starter)". */
export function describeStartingPrice(entry: CompetitorPricing): string {
  const paid = entry.plans
    .map((plan) => ({ plan, price: planPrice(plan) }))
    .filter((item): item is { plan: CompetitorPlan; price: number } => item.price !== null && item.price > 0)
    .sort((a, b) => a.price - b.price)[0];
  if (!paid) return 'Custom';
  const unit = paid.plan.unit === 'per-seat' ? ' per seat/month' : '/month flat';
  const annual = paid.plan.priceMonthly === null ? ', billed annually' : '';
  const noVideo = paid.plan.excludesVideo ? ', no video' : '';
  return `${formatPricingAmount(paid.price, entry.currency)}${unit} (${paid.plan.name}${annual}${noVideo})`;
}

/** Plan price as shown in tables: "Free", "Custom", "$15 per seat/month", "$199/month, billed annually". */
export function describePlanPrice(plan: CompetitorPlan, currency: PricingCurrency): string {
  if (plan.isFree) return 'Free';
  const fmt = (amount: number) => formatPricingAmount(amount, currency);
  const unit = plan.unit === 'per-seat' ? ' per seat/month' : '/month';
  if (plan.priceMonthly !== null && plan.priceMonthly !== undefined) {
    const annual =
      plan.priceAnnualMonthly !== undefined ? ` (${fmt(plan.priceAnnualMonthly)} billed annually)` : '';
    return `${fmt(plan.priceMonthly)}${unit}${annual}`;
  }
  if (plan.priceAnnualMonthly !== undefined) {
    return `${fmt(plan.priceAnnualMonthly)}${unit}, billed annually`;
  }
  return 'Custom';
}

/**
 * Billed-annually monthly total for the plan `getMonthlyCostForSeats` picked, when the vendor lists an annual
 * price for it. `null` when no annual price is listed.
 */
export function getAnnualMonthlyCostForSeats(entry: CompetitorPricing, seats: number, cost: SeatCost): number | null {
  if (cost.total === null || cost.planName === null) return null;
  const plan = entry.plans.find((item) => item.name === cost.planName);
  if (!plan || plan.priceAnnualMonthly === undefined) return null;
  if (plan.unit === 'flat') return plan.priceAnnualMonthly;
  const billedSeats = Math.max(Math.ceil(seats), plan.minSeats ?? 1);
  return Math.round(plan.priceAnnualMonthly * billedSeats * 100) / 100;
}
